import { Component, signal, OnInit, inject, LOCALE_ID, computed, effect, untracked, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductFilters } from './product-filters/product-filters';
import { ProductCardComponent } from './product-card-component/product-card-component';
import { Loading } from '../../../../utils/loading/loading';
import { EmptyData } from '../../../../utils/empty-data/empty-data';
import { finalize } from 'rxjs';
import { ProductService } from '../../../../services/product/product.service';
import {
  CustomerProductFilters,
  CustomerProductListItem,
} from '../../../../pages/user-dashboard/user-products/customer-product.model';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { PharmacistCartService } from '../../../../services/cart/cart.pharmacist';
import { CartIcon } from '../../../../shared/ui/icons/cart-icon/cart-icon';
import { Error } from '../../../public-user-component/auth/shared/error/error';
import { CartDto } from '../../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { FormsModule } from '@angular/forms';
import { PharmacyCreditService } from '../../../../services/pharmacy-credit/pharmacy-credit';
import { addItemToCartFnReducer } from '../../../../store/cart/cart.state';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-product-list-component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ProductFilters,
    Loading,
    EmptyData,
    ErrorData,
    ProductCardComponent,
    CartIcon,
    Error,
  ],
  templateUrl: './product-list-component.html',
  styleUrl: './product-list-component.scss',
})
export class ProductListComponent implements OnInit {
  private productService = inject(ProductService);
  private localeId = inject(LOCALE_ID);
  private pharmacistCartService = inject(PharmacistCartService);
  private pharmacyCreditService = inject(PharmacyCreditService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);

  selectedPharmacy = computed(() => this.pharmacyCreditService.selectedPharmacy());
  private storeId = computed(() => this.selectedPharmacy()?.id ?? null);
  private loadedStoreId: number | null | undefined;

  isAddingToCart = signal(false);

  products = signal<CustomerProductListItem[]>([]);
  isLoading = signal(false);
  totalRecords = signal(0);
  isArabic = computed(() => this.localeId.startsWith('ar'));
  hasError = signal(false);
  isErrorCart = signal('');
  errorQty = signal('');
  itemQuantity = signal('1');

  pageNum = signal(0);
  pageSize = signal(25);

  presetCompanyId = signal<number | undefined>(undefined);
  presetCompanyTitle = signal<string>('');
  presetCategoryId = signal<number | undefined>(undefined);
  presetCategoryTitle = signal<string>('');
  showPresetBanner = signal<boolean>(false);

  currentFilters = signal<CustomerProductFilters>({
    orderBy: 'CREATION_DATE',
    orderDir: 'DESC',
  });

  addToCartPopup = signal<{
    active: boolean;
    productId: string;
    productPrice: number;
    unitPrice: number;
    isOffer: boolean;
    offerId?: string;
    maxQuantity: number;
  } | null>(null);

  constructor() {
    // The order limit badges depend on the Store-Id, and the header selects the pharmacy after the page loads
    effect(() => {
      const storeId = this.storeId();
      untracked(() => {
        if (this.loadedStoreId !== undefined && storeId !== this.loadedStoreId) this.loadProducts();
      });
    });
  }

  ngOnInit() {
    const queryParams = this.route.snapshot.queryParamMap;
    const mfgCompanyId = queryParams.get('mfgCompanyId');
    const categoryId = queryParams.get('categoryId');

    const presetFilters: CustomerProductFilters = {
      orderBy: 'CREATION_DATE',
      orderDir: 'DESC',
    };

    let hasPreset = false;

    if (mfgCompanyId) {
      const titleEn = queryParams.get('mfgCompanyTitleEn') ?? '';
      const titleAr = queryParams.get('mfgCompanyTitleAr') ?? '';

      this.presetCompanyId.set(Number(mfgCompanyId));
      this.presetCompanyTitle.set(this.isArabic() ? titleAr || titleEn : titleEn);
      presetFilters.mfgCompanyId = Number(mfgCompanyId);
      hasPreset = true;
    }

    if (categoryId) {
      const titleEn = queryParams.get('categoryTitleEn') ?? '';
      const titleAr = queryParams.get('categoryTitleAr') ?? '';

      this.presetCategoryId.set(Number(categoryId));
      this.presetCategoryTitle.set(this.isArabic() ? titleAr || titleEn : titleEn);
      presetFilters.categoryId = Number(categoryId);
      hasPreset = true;
    }

    this.showPresetBanner.set(hasPreset);
    this.currentFilters.set(presetFilters);
    this.loadProducts();
  }

  clearPresetFilters() {
    this.onResetFilters();
  }

  loadProducts() {
    this.isLoading.set(true);
    this.hasError.set(false);

    const filters: CustomerProductFilters = {
      ...this.currentFilters(),
      pageNum: this.pageNum(),
      pageSize: this.pageSize(),
    };

    this.loadedStoreId = this.storeId();
    this.productService
      .getAllCustomerProducts(filters, this.isArabic() ? 'ar' : 'en', this.loadedStoreId)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.products.set(res.list);
          this.totalRecords.set(res.total);
          this.hasError.set(false);
        },
        error: () => {
          this.products.set([]);
          this.totalRecords.set(0);
          this.hasError.set(true);
        },
      });
  }

  onApplyFilters(filters: CustomerProductFilters) {
    this.showPresetBanner.set(false);

    this.currentFilters.set(filters);
    this.pageNum.set(0);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        mfgCompanyId: filters.mfgCompanyId ?? null,
        categoryId: filters.categoryId ?? null,
      },
      queryParamsHandling: 'merge',
    });

    this.loadProducts();
  }

  onResetFilters() {
    this.presetCompanyId.set(undefined);
    this.presetCompanyTitle.set('');
    this.presetCategoryId.set(undefined);
    this.presetCategoryTitle.set('');
    this.showPresetBanner.set(false);

    this.currentFilters.set({ orderBy: 'CREATION_DATE', orderDir: 'DESC' });
    this.pageNum.set(0);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.loadProducts();
  }

  onToggleSortOrder() {
    this.loadProducts();
  }

  onAddToCart() {
    this.isAddingToCart.set(true);

    const dto: CartDto = !this.addToCartPopup()?.isOffer
      ? {
          items: [
            {
              type: 'product',
              customerProductId: +this.addToCartPopup()?.productId!,
              quantity: +this.itemQuantity(),
            },
          ],
        }
      : {
          items: [
            {
              type: 'offer',
              offerId: +this.addToCartPopup()?.offerId!,
              products: [
                {
                  customerProductId: +this.addToCartPopup()?.productId!,
                  quantity: +this.itemQuantity(),
                },
              ],
            },
          ],
        };
    this.pharmacistCartService
      .addItemToCart('product', this.selectedPharmacy()?.id!, dto)
      .pipe(
        finalize(() => {
          this.isAddingToCart.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.addToCartPopup.set(null);

          this.store.dispatch(
            addItemToCartFnReducer({
              itemType: 'product',
              pharmacyId: this.selectedPharmacy()?.id!,
              dto,
            }),
          );
        },
        error: (err) => {
          this.isErrorCart.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ' : err.messageEn || 'some error occured',
          );
        },
      });
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords() / this.pageSize());
  }

  get currentPage(): number {
    return this.pageNum() + 1;
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.pageNum.set(page - 1);
      this.loadProducts();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.pageNum.update((n) => n + 1);
      this.loadProducts();
    }
  }

  previousPage() {
    if (this.pageNum() > 0) {
      this.pageNum.update((n) => n - 1);
      this.loadProducts();
    }
  }

  getPageNumbers(): number[] {
    const totalPages = this.totalPages;
    const current = this.currentPage;
    const delta = 2;
    const range: number[] = [];
    const rangeWithDots: number[] = [];
    let l: number;

    range.push(1);
    if (totalPages <= 1) return range;

    for (let i = current - delta; i <= current + delta; i++) {
      if (i < totalPages && i > 1) range.push(i);
    }
    range.push(totalPages);

    for (const i of range) {
      if (l!) {
        if (i - l === 2) rangeWithDots.push(l + 1);
        else if (i - l !== 1) rangeWithDots.push(-1);
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  }

  onActiveAddToCartPopup(e: {
    productId: string;
    quantity: string;
    unitPrice: number;
    isOffer: boolean;
    offerId?: string;
    remainingQuantity?: number | null;
  }) {
    this.isErrorCart.set('');
    this.itemQuantity.set('1');
    this.addToCartPopup.set({
      productId: e.productId,
      active: true,
      productPrice: e.unitPrice,
      unitPrice: e.unitPrice,
      isOffer: e.isOffer,
      offerId: e.offerId,
      maxQuantity: Math.min(100, e.remainingQuantity ?? 100),
    });
  }

  private maxQuantityError(max: number) {
    return this.isArabic() ? `يجب ألا تتجاوز القيمة ${max}` : `Value must not exceed ${max}`;
  }

  onQuantityChange(value: string) {
    this.errorQty.set('');
    const max = this.addToCartPopup()?.maxQuantity ?? 100;
    if (+value > max) this.errorQty.set(this.maxQuantityError(max));
    if (+value < 1)
      this.errorQty.set(this.isArabic() ? 'يجب أن تكون القيمة أكبر من صفر' : 'Value must be greater than 0');

    this.itemQuantity.set(value);
    this.addToCartPopup.update((prevData) => {
      if (!prevData) return null;
      return {
        ...prevData,
        productPrice: +value * prevData.unitPrice,
      };
    });
  }

  increaseQty() {
    this.errorQty.set('');
    const max = this.addToCartPopup()?.maxQuantity ?? 100;

    if (+this.itemQuantity() >= max) {
      this.errorQty.set(this.maxQuantityError(max));
    }
    if (+this.itemQuantity() < max) {
      this.itemQuantity.set((+this.itemQuantity() + 1).toString());
      this.addToCartPopup.update((prevData) => {
        if (!prevData) return null;

        return {
          ...prevData,
          productPrice: +this.itemQuantity() * prevData.unitPrice,
        };
      });
    }
  }

  decreaseQty() {
    this.errorQty.set('');
    if (+this.itemQuantity() === 1) {
      this.errorQty.set(this.isArabic() ? 'يجب أن تكون القيمة أكبر من صفر' : 'Value must be greater than 0');
    }
    if (+this.itemQuantity() > 1) {
      this.itemQuantity.set((+this.itemQuantity() - 1).toString());

      this.addToCartPopup.update((prevData) => {
        if (!prevData) return null;

        return {
          ...prevData,
          productPrice: +this.itemQuantity() * prevData.unitPrice,
        };
      });
    }
  }

  errorQuantity() {
    const qty = Number(this.itemQuantity());

    if (!Number.isInteger(qty)) {
      this.errorQty.set(this.isArabic() ? 'يرجى إدخال رقمًا صحيحًا' : 'Please enter a valid number.');

      return;
    }

    this.isErrorCart.set('');
  }
}
